import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useActivePortal } from "../contexts/ThemeContext";

/* ─── Design Tokens ───
 *
 * V2 ADR-003 Phase 4.3 wave 4 — TwoRailSidebar carries its own design
 * system (separate palette from `@realtyflows/ui`). `defaultC` is the
 * baseline; `useC()` merges in per-portal overrides when the active
 * portal has `primary_color` / `accent_color` set.
 */
const defaultC = {
  ivory: "#F8F5F0",
  ivoryDark: "#EDE9E2",
  ivoryBorder: "#DDD8CE",
  navy: "#1B2B4B",
  navyMid: "#2D4270",
  navyLight: "#3D5A96",
  emerald: "#1A7A5E",
  emeraldLight: "#22A07C",
  emeraldBg: "#EAF5F0",
  textPrimary: "#1B2B4B",
  textSecondary: "#5A6B8A",
  textMuted: "#8A98B0",
  amber: "#C8872A",
};

/**
 * Merge per-portal brand colors over the defaults. Concerns mirror what
 * the main ThemeContext does:
 *   - primary_color → the emerald family (accent green by default)
 *   - accent_color  → the navy family (dark rail by default)
 * Ivory neutrals + textMuted intentionally stay constant for legibility.
 */
function useC() {
  const m = useActivePortal();
  if (!m) return defaultC;
  return {
    ...defaultC,
    ...(m.primary_color && {
      emerald: m.primary_color,
      emeraldLight: m.primary_color,
      emeraldBg: m.primary_color + "1A",   // ~10% tint for hover bgs
    }),
    ...(m.accent_color && {
      navy: m.accent_color,
      navyMid: m.accent_color + "CC",      // ~80% for mid-tone areas
      navyLight: m.accent_color + "99",    // ~60% for the lighter rail
      textPrimary: m.accent_color,
    }),
  };
}

const f = {
  ui: "'Plus Jakarta Sans', sans-serif",
  heading: "'Instrument Serif', serif",
  mono: "'JetBrains Mono', monospace",
};

/* ─── SVG Icons (same as classic sidebar) ─── */
const SvgIcon = ({ children, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const navIcons = {
  dashboard: <SvgIcon><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></SvgIcon>,
  contracts: <SvgIcon><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></SvgIcon>,
  clients: <SvgIcon><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></SvgIcon>,
  deals: <SvgIcon><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></SvgIcon>,
  emails: <SvgIcon><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></SvgIcon>,
  appointments: <SvgIcon><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></SvgIcon>,
  team: <SvgIcon><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></SvgIcon>,
  settings: <SvgIcon><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></SvgIcon>,
  pricing: <SvgIcon><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></SvgIcon>,
  logout: <SvgIcon><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></SvgIcon>,
};

/* ─── Font Loader + Keyframes ─── */
const fontImport = `@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@400;500&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
@keyframes rfSlideIn {
  from { opacity: 0; transform: translateX(-4px); }
  to { opacity: 1; transform: translateX(0); }
}`;

/* ─── Build navigation data from props ─── */
function buildCategories(plan, emailUnread) {
  const tierLevel = { starter: 1, professional: 2, team: 3, enterprise: 4 };
  const isPro = (tierLevel[plan] || 0) >= 2;

  const main = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: navIcons.dashboard,
      items: [{ key: "overview", label: "Overview", path: "/dashboard" }],
    },
    {
      key: "contracts",
      label: "Contracts",
      icon: navIcons.contracts,
      items: [
        { key: "all-contracts", label: "All Contracts", path: "/contracts" },
        { key: "templates", label: "Templates", path: "/contracts/templates" },
      ],
    },
    {
      key: "clients",
      label: "Clients",
      icon: navIcons.clients,
      items: [{ key: "all-clients", label: "All Clients", path: "/clients" }],
    },
    {
      key: "deals",
      label: "Deals",
      icon: navIcons.deals,
      items: [
        { key: "all-deals", label: "All Deals", path: "/deals" },
        { key: "new-deal", label: "New Deal", path: "/deals/new" },
        { key: "pipeline-settings", label: "Pipeline Settings", path: "/deals/pipelines" },
      ],
    },
    {
      key: "emails",
      label: "Emails",
      icon: navIcons.emails,
      badge: emailUnread || 0,
      items: [{ key: "inbox", label: "Inbox", path: "/emails" }],
    },
    {
      key: "appointments",
      label: "Appointments",
      icon: navIcons.appointments,
      items: [{ key: "calendar", label: "My Calendar", path: "/appointments" }],
    },
    {
      key: "team",
      label: "Team",
      icon: navIcons.team,
      items: [{ key: "members", label: "Team Members", path: "/team" }],
    },
  ];

  const settings = {
    key: "settings",
    label: "Settings",
    icon: navIcons.settings,
    items: [
      { key: "general", label: "General", path: "/settings" },
      ...(isPro ? [{ key: "automations", label: "Automations", path: "/settings/automations" }] : []),
    ],
  };

  const pricing = {
    key: "pricing",
    label: "Upgrade Plan",
    icon: navIcons.pricing,
    items: [{ key: "plans", label: "Plans & Pricing", path: "/pricing" }],
  };

  return { main, settings, pricing };
}

/* ─── Determine active category from URL ─── */
function getActiveFromPath(pathname, categories, settings, pricing) {
  const all = [...categories, settings, pricing];
  for (const cat of all) {
    for (const item of cat.items) {
      if (pathname === item.path || pathname.startsWith(item.path + "/")) {
        return { category: cat.key, item: item.key };
      }
    }
  }
  // Fallback: match by first path segment
  const seg = "/" + pathname.split("/")[1];
  for (const cat of all) {
    for (const item of cat.items) {
      if (item.path.startsWith(seg)) {
        return { category: cat.key, item: item.key };
      }
    }
  }
  return { category: "dashboard", item: "overview" };
}

/* ─── Tooltip ─── */
function Tooltip({ text, visible }) {
  const c = useC();
  if (!visible) return null;
  return (
    <div style={{
      position: "absolute",
      left: "calc(100% + 8px)",
      top: "50%",
      transform: "translateY(-50%)",
      background: c.navy,
      color: "#fff",
      fontSize: 12,
      fontFamily: f.ui,
      fontWeight: 500,
      padding: "5px 10px",
      borderRadius: 20,
      whiteSpace: "nowrap",
      pointerEvents: "none",
      zIndex: 1000,
      boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
    }}>
      {text}
    </div>
  );
}

/* ─── Category Icon Button ─── */
function CategoryIcon({ cat, active, onSelect, hovered, onHover, onLeave, tooltipVisible, badge }) {
  const c = useC();
  return (
    <div
      style={{ position: "relative", display: "flex", justifyContent: "center" }}
      onMouseEnter={() => onHover(cat.key)}
      onMouseLeave={onLeave}
    >
      <button
        onClick={() => onSelect(cat.key)}
        style={{
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 8,
          border: "none",
          cursor: "pointer",
          background: active
            ? "rgba(255,255,255,0.12)"
            : hovered
            ? "rgba(255,255,255,0.07)"
            : "transparent",
          color: active ? "#fff" : "rgba(255,255,255,0.55)",
          transition: "background 0.15s ease, color 0.15s ease",
          padding: 0,
        }}
      >
        {cat.icon}
      </button>
      {badge > 0 && (
        <div style={{
          position: "absolute", top: 0, right: -2,
          width: 8, height: 8, borderRadius: "50%",
          background: c.emerald, border: `2px solid ${c.navy}`,
        }} />
      )}
      <Tooltip text={cat.label} visible={tooltipVisible} />
    </div>
  );
}

/* ─── Sub-item Button ─── */
function SubItem({ item, active, onClick, badge, animDelay }) {
  const c = useC();
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={() => onClick(item)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "calc(100% - 12px)",
        margin: "1px 6px",
        padding: "7px 14px",
        borderRadius: 6,
        border: "none",
        cursor: "pointer",
        background: active ? c.emeraldBg : hovered ? c.ivoryDark : "transparent",
        color: active ? c.emerald : c.textSecondary,
        fontSize: 12,
        fontFamily: f.ui,
        fontWeight: active ? 500 : 400,
        textAlign: "left",
        transition: "background 0.12s ease, color 0.12s ease",
        animation: `rfSlideIn 150ms ease ${animDelay}ms both`,
      }}
    >
      <span>{item.label}</span>
      {badge > 0 && (
        <span style={{
          background: c.emerald,
          color: "#fff",
          fontSize: 10,
          fontFamily: f.mono,
          fontWeight: 500,
          padding: "1px 6px",
          borderRadius: 10,
          minWidth: 18,
          textAlign: "center",
          lineHeight: "16px",
        }}>
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );
}

/* ─── Main Component ─── */
export default function TwoRailSidebar({ user, plan, onLogout, emailUnread, activeWorkspace, onWorkspaceChange }) {
  const c = useC();
  const location = useLocation();
  const navigate = useNavigate();
  const [hoveredCat, setHoveredCat] = useState(null);
  const [tooltipCat, setTooltipCat] = useState(null);
  const tooltipTimer = useRef(null);
  const [animKey, setAnimKey] = useState(0);
  const [selectedCat, setSelectedCat] = useState(null);

  const { main, settings, pricing } = buildCategories(plan, emailUnread);
  const active = getActiveFromPath(location.pathname, main, settings, pricing);

  // Selected category: either manually selected or derived from URL
  const currentCatKey = selectedCat || active.category;
  const allCats = [...main, settings, pricing];
  const activeCatData = allCats.find((cat) => cat.key === currentCatKey) || main[0];

  // Reset selected category when URL changes
  useEffect(() => {
    setSelectedCat(null);
  }, [location.pathname]);

  const handleCategorySelect = useCallback((key) => {
    setSelectedCat(key);
    setAnimKey((k) => k + 1);
    // Navigate to first item of this category
    const cat = [...main, settings, pricing].find((c) => c.key === key);
    if (cat && cat.items.length > 0) {
      navigate(cat.items[0].path);
    }
  }, [main, settings, pricing, navigate]);

  const handleItemClick = useCallback((item) => {
    navigate(item.path);
  }, [navigate]);

  const handleHover = useCallback((key) => {
    setHoveredCat(key);
    tooltipTimer.current = setTimeout(() => {
      setTooltipCat(key);
    }, 300);
  }, []);

  const handleLeave = useCallback(() => {
    setHoveredCat(null);
    setTooltipCat(null);
    if (tooltipTimer.current) {
      clearTimeout(tooltipTimer.current);
      tooltipTimer.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
    };
  }, []);

  // Get initials from user name
  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "?";

  return (
    <>
      <style>{fontImport}</style>
      <div style={{
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
        display: "flex",
        zIndex: 100,
      }}>
        {/* ─── Rail 1: Icon Rail ─── */}
        <div style={{
          width: 56,
          background: c.navy,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingTop: 12,
          paddingBottom: 12,
          boxSizing: "border-box",
          overflowY: "auto",
          overflowX: "hidden",
          height: "100%", minHeight: 0,
        }}>
          {/* Logo */}
          <div
            onClick={() => navigate("/dashboard")}
            style={{
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
              userSelect: "none",
              cursor: "pointer",
            }}
          >
            <img src="/logo.png" alt="RF" style={{ width: 24, height: 24, borderRadius: 4 }} />
          </div>

          {/* Main Categories */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            alignItems: "center",
            flex: 1,
          }}>
            {main.map((cat) => (
              <CategoryIcon
                key={cat.key}
                cat={cat}
                active={active.category === cat.key}
                onSelect={handleCategorySelect}
                hovered={hoveredCat === cat.key}
                onHover={handleHover}
                onLeave={handleLeave}
                tooltipVisible={tooltipCat === cat.key}
                badge={cat.badge}
              />
            ))}
          </div>

          {/* Bottom Section */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            alignItems: "center",
            marginTop: "auto",
            paddingTop: 8,
          }}>
            {/* Pricing */}
            <CategoryIcon
              cat={pricing}
              active={active.category === "pricing"}
              onSelect={handleCategorySelect}
              hovered={hoveredCat === "pricing"}
              onHover={handleHover}
              onLeave={handleLeave}
              tooltipVisible={tooltipCat === "pricing"}
            />

            {/* Settings */}
            <CategoryIcon
              cat={settings}
              active={active.category === "settings"}
              onSelect={handleCategorySelect}
              hovered={hoveredCat === "settings"}
              onHover={handleHover}
              onLeave={handleLeave}
              tooltipVisible={tooltipCat === "settings"}
            />

            {/* Logout */}
            <div
              style={{ position: "relative", display: "flex", justifyContent: "center" }}
              onMouseEnter={() => handleHover("__logout__")}
              onMouseLeave={handleLeave}
            >
              <button
                onClick={onLogout}
                style={{
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  background: hoveredCat === "__logout__" ? "rgba(220,38,38,0.15)" : "transparent",
                  color: hoveredCat === "__logout__" ? "#f87171" : "rgba(255,255,255,0.4)",
                  transition: "background 0.15s ease, color 0.15s ease",
                  padding: 0,
                }}
              >
                {navIcons.logout}
              </button>
              <Tooltip text="Logout" visible={tooltipCat === "__logout__"} />
            </div>

            {/* Agent Avatar */}
            <div
              style={{ position: "relative" }}
              onMouseEnter={() => handleHover("__agent__")}
              onMouseLeave={handleLeave}
            >
              <div
                onClick={() => navigate("/settings")}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: c.navyMid,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  marginTop: 4,
                  border: "2px solid rgba(255,255,255,0.15)",
                }}
              >
                <span style={{
                  fontSize: 10,
                  fontFamily: f.ui,
                  fontWeight: 600,
                  color: "#fff",
                  letterSpacing: "0.3px",
                }}>
                  {initials}
                </span>
              </div>
              <Tooltip
                text={`${user?.name || "User"} — ${user?.role || "Agent"}`}
                visible={tooltipCat === "__agent__"}
              />
            </div>
          </div>
        </div>

        {/* ─── Rail 2: Sub-nav Rail ─── */}
        <div style={{
          width: 200,
          background: "#fff",
          borderRight: `1px solid ${c.ivoryBorder}`,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
          boxSizing: "border-box",
          height: "100%", minHeight: 0,
        }}>
          {/* Section Header */}
          <div style={{
            padding: "16px 14px 4px",
            fontSize: 9,
            fontFamily: f.ui,
            fontWeight: 700,
            color: c.textMuted,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            userSelect: "none",
          }}>
            {activeCatData.label}
          </div>

          {/* Sub-items */}
          <div key={`${currentCatKey}-${animKey}`} style={{ paddingTop: 2, paddingBottom: 12 }}>
            {activeCatData.items.map((item, i) => {
              const isActive = location.pathname === item.path ||
                (item.path !== "/" && location.pathname.startsWith(item.path + "/"));
              return (
                <SubItem
                  key={item.key}
                  item={item}
                  active={isActive}
                  onClick={handleItemClick}
                  badge={item.key === "inbox" ? emailUnread : 0}
                  animDelay={i * 20}
                />
              );
            })}
          </div>

          {/* Bottom: Plan badge + branding */}
          <div style={{
            marginTop: "auto",
            padding: "12px 14px",
            borderTop: `1px solid ${c.ivoryBorder}`,
          }}>
            {plan && (
              <div style={{
                fontSize: 10,
                fontFamily: f.mono,
                fontWeight: 500,
                color: c.emerald,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 6,
              }}>
                {plan} plan
              </div>
            )}
            <div style={{
              fontSize: 10,
              fontFamily: f.heading,
              fontStyle: "italic",
              color: c.textMuted,
              letterSpacing: "0.3px",
            }}>
              RealtyFlows
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
